/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import React, {useContext, useEffect, useLayoutEffect, useRef, useState} from "react";
import Player from "@vimeo/player";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import {useIntl} from "react-intl";

const useGeneralEditScoredSceneForm = (selectedScoredScene, selectedVideo, editedScoredScene) => {

    // Get permission-based limited association structure
    const {allowedAssociationStructure} = useContext(FirebaseAuthContext);

    // Editable values - used to control the controlled elements' states
    const [scoreDefinitions, setScoreDefinitions] = useState(selectedScoredScene?.scoreDefinitions || [])
    const [difficulty, setDifficulty] = React.useState(selectedScoredScene?.basics?.difficulty || '');
    const [mainFocus, setMainFocus] = React.useState(selectedScoredScene?.basics?.mainFocus || []);
    const [trailingPeriod, setTrailingPeriod] = React.useState(2); // hardcoded to 2 - to make it flexible in the future, use: (selectedScoredScene?.basics?.trailingPeriod || 0)
    const [athleteLeftPosition, setAthleteLeftPosition] = React.useState(selectedScoredScene?.basics?.athleteLeftPosition || 'red');
    const [blurringDefinition, setBlurringDefinition] = useState(selectedScoredScene?.basics?.blurring || null)

    const [videoHeight, setVideoHeight] = useState(0)
    const [videoWidth, setVideoWidth] = useState(0)

    // Information about the discipline
    const disciplineStructure = allowedAssociationStructure[selectedVideo.basics.association].disciplines.find((d) => d.name === selectedVideo.basics.discipline)

    const [videoRange, setVideoRange] = useState(
        selectedScoredScene?.basics
            ? [selectedScoredScene?.basics?.sceneStart, selectedScoredScene?.basics.sceneEnd]
            : [])

    const [videoProgress, setVideoProgress] = useState(0) // Video progress is always in seconds (with decimal places)
    const [videoPlaying, setVideoPlaying] = useState(false)
    const videoPlayingRef = useRef(false) // Holds the same boolean value as the videoPlaying state (required for the Vimeo autostart-bug workaround)

    // Default playback rate
    const [defaultPlaybackRate, setDefaultPlaybackRate] = useState(1.0)
    const [isSlowPlaybackRate, setIsSlowPlaybackRate] = useState(false)

    // Preview
    const previewScoreDefinition = useRef(null)
    const [previewInProgress, setPreviewInProgress] = useState(false)

    // Show highlighting while video is playing
    const [videoProgressInHighlightingRange, setVideoProgressInHighlightingRange] = useState(false)

    const [originalVideoDuration, setOriginalVideoDuration] = useState()
    const originalVideoDurationRef = useRef()

    const [screenReady, setScreenReady] = useState(false)

    const videoPlayer = useRef(null)

    // Drawing
    const [drawingMode, setDrawingMode] = useState(false)
    const [activeDrawingScoreDefinition, setActiveDrawingScoreDefinition] = useState(null)

    // Blurring
    const [blurDrawingMode, setBlurDrawingMode] = useState(false)
    const [showBlurPreview, setShowBlurPreview] = useState(false)

    // Dimensions and position of the canvas
    const [canvasWidth, setCanvasWidth] = useState(0)
    const [canvasHeight, setCanvasHeight] = useState(0)
    const [canvasLeft, setCanvasLeft] = useState(0)
    const [canvasTop, setCanvasTop] = useState(0)

    // The currently selected tab index of the score definition tabs (starting with 0)
    const [activeScoreDefinitionIndex, setActiveScoreDefinitionIndex] = useState(0)

    // Initialize the provided reference with the current scored scene data
    editedScoredScene.current = {
        scoreDefinitions: scoreDefinitions,
        videoRange: videoRange,
        difficulty: difficulty,
        mainFocus: mainFocus,
        trailingPeriod: trailingPeriod,
        athleteLeftPosition: athleteLeftPosition,
        blurring: blurringDefinition,
        key: selectedScoredScene?.key
    }

    // Full screen mode on/off
    const [fullScreenMode, setFullScreenMode] = useState(false)

    // The width of the video cut slider to properly visualize the range of already created scored scenes for this video
    const [videoCutSliderWidth, setVideoCutSliderWidth] = useState(0)

    // Only used in case something unexpected happened
    const {enqueueSnackbar} = useSnackbar();
    const history = useHistory();

    // Internationalization for simple strings
    const intl = useIntl();

    // For all already persisted score definitions, the frame has obviously already been seeked before
    useEffect(() => {
        scoreDefinitions.forEach((scoreDefinition) => {
            scoreDefinition.frameAlreadySeeked = true
        })
    }, [])

    // Whenever the default playback rate state is changed, the actual video playback rate needs to be set accordingly
    useEffect(() => {
        if (videoPlayer.current) {
            videoPlayer.current.setPlaybackRate(defaultPlaybackRate)
        }
    }, [defaultPlaybackRate])


    useEffect(() => {
        // Initialize the video player - needs to be done in useEffect, otherwise the playerID would not be found
        const iframe = document.querySelector('iframe');
        videoPlayer.current = new Player(iframe);

        // Setting the default playback rate according to the config
        videoPlayer.current.setPlaybackRate(defaultPlaybackRate)

        // Always set the current time (refreshed every 250ms) - therefore very resource intensive
        videoPlayer.current.on('timeupdate', (data) => {
            setVideoProgress(data.seconds)
        })
    }, [])

    useEffect(() => {

        // Video has reached end of defined range (only do this if the video is currently playing, otherwise moving the end-video-cut-thumb to the left would fulfill this condition)
        if (videoPlaying && videoProgress > videoRange[1]) {
            videoPlayer.current.pause().then(() => {
                setVideoPlaying(false)
                videoPlayingRef.current = false
                videoPlayer.current.setCurrentTime(videoRange[0])
            })
        }

        // Show Blurring preview if in blurring range (we access the editedScoredScene reference here, because this is initialized and always holds the current value)
        // Do not set the values if currently in the blur edit mode, because EXPANDING the visibility range will set the video progress out of the currently stored blurring range
        // and this would set the blur preview mode to false.
        if (!blurDrawingMode && videoProgress >= editedScoredScene?.current?.blurring?.visibilityStart && videoProgress <= editedScoredScene?.current?.blurring?.visibilityEnd) {
            setShowBlurPreview(true)
        } else {
            setShowBlurPreview(false)
        }

        // Show highlighting directly in the video frame in case it is in the respective time frame
        if (!drawingMode && !blurDrawingMode && !previewInProgress) {
            const currentScoreDefinition = getScoreDefinitionByVideoProgress(videoProgress)

            if (currentScoreDefinition?.highlighting) {
                setActiveDrawingScoreDefinition(currentScoreDefinition)
                setVideoProgressInHighlightingRange(true)
            } else {
                setActiveDrawingScoreDefinition(null)
                setVideoProgressInHighlightingRange(false)
            }
        }

        // The preview is currently in the highlighting range - *** show highlighting drawing ***
        if (previewScoreDefinition.current
            && previewScoreDefinition.current.highlighting
            && videoProgress >= previewScoreDefinition.current.highlighting.visibilityStart
            && videoProgress <= previewScoreDefinition.current.highlighting.visibilityEnd) {

            if (!activeDrawingScoreDefinition) {
                setActiveDrawingScoreDefinition(previewScoreDefinition.current)
            }
        }

        // The preview is out of the highlighting range - *** do not show the highlighting drawing anymore ***
        if (previewScoreDefinition.current
            && previewScoreDefinition.current.highlighting
            && videoProgress > previewScoreDefinition.current.highlighting.visibilityEnd) {

            if (activeDrawingScoreDefinition) {
                setActiveDrawingScoreDefinition(null)
            }
        }

        // Preview has ended - set it to the score definition time again and set to normal playback rate
        if (previewScoreDefinition.current && videoProgress > (previewScoreDefinition.current.time + 0.5)) {

            videoPlayer.current.pause()
                .then(() => {
                    // IMPORTANT: previewScoreDefinition.current might be null due to asynchronicity (i.e. it might enters the above condition a 2nd time)
                    if (!previewScoreDefinition.current) {
                        return
                    }

                    const resetTime = previewScoreDefinition.current.time

                    // it is important to set it to "null" before we set the "current time", because otherwise the if statement before will enable the highlighting again
                    previewScoreDefinition.current = null

                    return videoPlayer.current.setCurrentTime(resetTime)
                })
                .then(() => {
                    return videoPlayer.current.setPlaybackRate(defaultPlaybackRate)
                })
                .then(() => {
                    setPreviewInProgress(false)
                })

        }
    }, [videoProgress, drawingMode, blurDrawingMode, previewInProgress, videoPlaying])

    // Recalculate absolute dimensions in case of DOM changes (e.g. resize of window, changing from/to video full screen, etc)
    useLayoutEffect(() => {
        recalculateCanvasDimensions()
        recalculateVideoCutSliderDimensions()
    })

    const recalculateCanvasDimensions = () => {
        const videoPlayer = document.getElementById('iFrameContainer');

        if (!videoPlayer) {
            return;
        }

        const wFrame = videoPlayer.getBoundingClientRect().width
        const tFrame = videoPlayer.getBoundingClientRect().top
        const bFrame = videoPlayer.getBoundingClientRect().bottom

        setCanvasWidth(wFrame)
        setCanvasHeight(videoHeight / videoWidth * wFrame)
        setCanvasLeft(0)
        setCanvasTop((bFrame - tFrame - videoHeight / videoWidth * wFrame) / 2)
    }

    const recalculateVideoCutSliderDimensions = () => {
        const sliderElement = document.getElementById('videoCutSlider');
        if (sliderElement) {
            setVideoCutSliderWidth(sliderElement.getBoundingClientRect().width)
        }
    }

    /*
    Workaround - Problem 1:
    Workaround to get the exact duration of the video in milliseconds (because the getDuration() method only provides seconds).
    As additional positive-side effect, the video is already started once, which enables active seekings (as the video is already started)
    Otherwise only the progress bar is moving, but not the video itself!

    Workaround - Problem 2:
    Sometimes the play() method of the Vimeo Player SDK does not resolve the promise, which makes the promise chain being stuck.
    Preventing bug in Vimeo Player SDK:
      - The video is started in the background in any case
      - However, sometimes the play() function does not resolve the promise due to whatever reasons (which blocks subsequent then-chains).
      - Therefore, we are setting a timout - if the promise does not resolve within 10 seconds, it will be resolved automatically
     */
    useEffect(() => {
        videoPlayer.current.setVolume(0)
            .then((volume) => {

                // Fallback after 5 seconds in case the play() promise does not resolve
                return Promise.race([
                    videoPlayer.current.play(),
                    new Promise((resolve, reject) => setTimeout(() => {

                        // Only resolve the promise in case the video duration has already been set, otherwise reject the promise (just a paranoid security mechanism)
                        if (originalVideoDurationRef.current) {
                            resolve()
                        } else {
                            reject()
                        }
                    }, 5000))]
                )
            })
            .then(() => {
                videoPlayer.current.off('progress', initVideoDuration) // only de-register this specific listener
                return videoPlayer.current.pause()
            })
            .then(() => {

                // Throw an error in case the video duration has not been set with the precise milliseconds
                if (!originalVideoDurationRef.current) {
                    throw new Error()
                }

                setScreenReady(true)
                return videoPlayer.current.setCurrentTime(selectedScoredScene?.basics?.sceneStart || 0)
            })
            .then(() => {
                return videoPlayer.current.setVolume(0.25)
            })
            .catch((error) => {

                // Show error message + redirect the user to the video edit screen in case something went wrong while setting the exact video range
                enqueueSnackbar(intl.formatMessage({
                    id: 'app.scoredScene.messages.load.seekExactVideoDuration.error',
                    defaultMessage: 'Something went wrong while seeking for the exact video duration.'
                }), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 5000
                });
                history.push(`/admin/videos/${selectedVideo.key}/edit`)
            })

        /*
          Listener, which only serves for the purpose of setting the exact duration for the video slider.
          However, the listener is immediately de-registered after the video has ended - see code above.
         */
        videoPlayer.current.on('progress', initVideoDuration)

        /* Workaround for the Vimeo autostart-bug:
           When using the self-developed video-progress-slider for jumping to a desired video position in paused-state,
           the triggered "setCurrentTime" Vimeo SDK function seems to automatically start the video, although it was in
           state PAUSED. In order to avoid this possible autostart after seeking a video position, we check after each
           seeking (triggered by the Vimeo player's setCurrentTime function) the following inconsistent state, which is
           an indicator for such a buggy situation:
             - The actual video is not paused (i.e. running)
             - The videoPlaying reference is set to false (indicating the the video should actually not be playing)
            */
        videoPlayer.current.on('seeked', (data) => {

            // Check if the video is really in progress
            videoPlayer.current.getPaused().then(async (paused) => {
                if (!paused && !videoPlayingRef.current) {
                    videoPlayer.current.pause()
                }
            })
        })
    }, [])

    const initVideoDuration = (data) => {
        setOriginalVideoDuration(data.duration)
        originalVideoDurationRef.current = data.duration

        // Initialize video range according to exact duration in milliseconds
        // Only in case of a new scored scene - in case of an editing, the time range is already set properly
        if (videoRange.length === 0) {
            setVideoRange([0, data.duration])
            editedScoredScene.current.videoRange = [0, data.duration]
        }
    }

    useEffect(() => {
        videoPlayer.current.getVideoHeight().then((height) => {
            setVideoHeight(height)
        })
        videoPlayer.current.getVideoWidth().then((width) => {
            setVideoWidth(width)
        })
    }, [])

    const getScoreDefinitionByVideoProgress = (progress) => {
        const sdList = scoreDefinitions.filter((sd) => sd.highlighting && progress >= sd.highlighting.visibilityStart && progress <= sd.highlighting.visibilityEnd)
        if (sdList.length === 0) {
            return null
        }

        /*
        If the user exactly chooses the time (e.g. with the jump-to arrow-symbol), find the exact match.
        Otherwise simply return the first score definition.
         */
        const exactJump = sdList.find((sd) => sd.time === progress)

        if (exactJump) {
            return exactJump
        }

        return sdList[0]
    }

    const roundNumber = (number) => {
        return Math.round((number + Number.EPSILON) * 1000) / 1000;
    };

    const addScoreDefinition = () => {
        const newScores = [{
            isNew: true, // Temporary property just to identify the object after sorting to determine the index
            time: roundNumber(videoProgress),
            athlete: 'blue',
            score: '',
            technique: '',
            frameAlreadySeeked: false // this is only used in the frontend and not persisted in the DB!!!
        }, ...scoreDefinitions]
        newScores.sort((s1, s2) => s1.time - s2.time)

        // Set the new score definition state, by setting the isNew to "false" again
        setScoreDefinitions(newScores.map((sd) => {
            return {
                ...sd,
                isNew: false
            }
        }))
        editedScoredScene.current.scoreDefinitions = newScores

        // Set the index, so that the tab becomes active
        setActiveScoreDefinitionIndex(newScores.findIndex((sd) => sd.isNew))

        // Leave a possible full screen mode in order to show the decision panel again
        setFullScreenMode(false)

        // Pause the video in case it is not yet paused
        pauseVideo()
    }

    const addConcurrentScoreDefinition = () => {
        const newScores = [...scoreDefinitions, {
            concurrent: true,
            time: scoreDefinitions[0].time, // Take the time of the first score definition (which must exist - otherwise function is not executable)
            athlete: scoreDefinitions[0].athlete === 'blue' ? 'red' : 'blue',
            score: '',
            technique: '',
            frameAlreadySeeked: true // Automatically set to true, as no time will be selectable in case of concurrent decision
        }]
        setScoreDefinitions(newScores)
        editedScoredScene.current.scoreDefinitions = newScores

        // Set the index, so that the tab becomes active (new concurrent decision index is always 1 - i.e. at the end)
        setActiveScoreDefinitionIndex(1)

        // Leave a possible full screen mode in order to show the decision panel again
        setFullScreenMode(false)

        // Pause the video in case it is not yet paused
        pauseVideo()
    }

    const removeScoreDefinition = (index) => {
        const newScoreDefinitions = [...scoreDefinitions]
        newScoreDefinitions.splice(index, 1)

        setScoreDefinitions(newScoreDefinitions)
        editedScoredScene.current.scoreDefinitions = newScoreDefinitions

        // Set the index, so that the tab becomes active (always set it to the first tab by default - as one tab always needs to exist in case of deletion is pressed)
        setActiveScoreDefinitionIndex(0)
    }

    const pictureForward = (index) => {
        if (scoreDefinitions[index].time + 0.02 < videoRange[1]) {
            videoPlayer.current.setCurrentTime(scoreDefinitions[index].time + 0.02)
            handleChangeTime(scoreDefinitions[index].time + 0.02, index, true)
        } else {
            videoPlayer.current.setCurrentTime(videoRange[1])
            handleChangeTime(videoRange[1], index, true)
        }
    }

    const pictureBack = (index) => {
        if (scoreDefinitions[index].time - 0.02 > videoRange[0]) {
            videoPlayer.current.setCurrentTime(scoreDefinitions[index].time - 0.02)
            handleChangeTime(scoreDefinitions[index].time - 0.02, index, true)
        } else {
            videoPlayer.current.setCurrentTime(videoRange[0])
            handleChangeTime(videoRange[0], index, true)
        }
    }

    const jumpToScoreDefinition = (index) => {
        videoPlayer.current.setCurrentTime(scoreDefinitions[index].time)

        // only done for performance/usability reasons (because the update of the player anyway triggers the update of the progress)
        setVideoProgress(scoreDefinitions[index].time)
    }

    const updateVideoRange = (event, data, activeThumb) => {
        setVideoRange(data)
        editedScoredScene.current.videoRange = data

        videoPlayer.current.setCurrentTime(data[activeThumb])
            .then(() => videoPlayer.current.pause())
    }

    const updateVideoProgress = (seconds) => {
        // The check is necessary due to the extended progress bar in case there is a *trailing period* added
        if (seconds <= videoRange[1]) {
            videoPlayer.current.setCurrentTime(seconds)

            // only done for performance/usability reasons (because the update of the player anyway triggers the update of the progress)
            // uncomment again if usability troubles arise
            //setVideoProgress(data)
        }
    }

    const playVideo = () => {
        videoPlayer.current.play()
        setVideoPlaying(true)
        videoPlayingRef.current = true
    }

    const pauseVideo = () => {
        videoPlayer.current.pause()
        setVideoPlaying(false)
        videoPlayingRef.current = false

        // IMPORTANT, as otherwise the actual videoProgress time will be slightly different to the time passed to handleChangeTime
        videoPlayer.current.setCurrentTime(videoProgress)
    }

    const setCurrentVideoTimeInScoreDefinition = (index) => {
        handleChangeTime(videoProgress, index, false)
    }

    const fastForward = () => {
        if (videoProgress + 0.02 < videoRange[1]) {
            videoPlayer.current.setCurrentTime(videoProgress + 0.02)
        } else {
            videoPlayer.current.setCurrentTime(videoRange[1])
        }
    }

    const fastRewind = () => {
        if (videoProgress - 0.02 > videoRange[0]) {
            videoPlayer.current.setCurrentTime(videoProgress - 0.02)
        } else {
            videoPlayer.current.setCurrentTime(videoRange[0])
        }
    }

    /*
    Functions for controlling the state of the controlled input elements.
     */

    const handleChangeDifficulty = (event) => {
        setDifficulty(event.target.value)

        editedScoredScene.current = {
            ...editedScoredScene.current,
            difficulty: event.target.value
        }
    }

    const handleChangeMainFocus = (value) => {
        setMainFocus(value)

        editedScoredScene.current = {
            ...editedScoredScene.current,
            mainFocus: value
        }
    }

    const handleChangeTrailingPeriod = (event) => {
        if (event.target.value >= 0 && event.target.value <= 5) {
            setTrailingPeriod(parseInt(event.target.value))

            editedScoredScene.current = {
                ...editedScoredScene.current,
                trailingPeriod: parseInt(event.target.value)
            }
        }
    }

    const switchAthleteLeftPosition = () => {
        editedScoredScene.current = {
            ...editedScoredScene.current,
            athleteLeftPosition: athleteLeftPosition === 'blue' ? 'red' : 'blue'
        }

        athleteLeftPosition === 'blue' ? setAthleteLeftPosition('red') : setAthleteLeftPosition('blue')
    }

    const handleChangeTime = (selectedTime, index, shouldSetFrameAlreadySeeked) => {
        if (scoreDefinitions && scoreDefinitions.length > 0 && index !== -1) {
            const newScores = [...scoreDefinitions]

            if (disciplineStructure.mode === 'point_stop') {

                // In case of point_stop automatically update all entries (i.e. one or optionally an additional concurrent score definition)
                newScores.forEach((scoreDef) => {
                    scoreDef.time = roundNumber(selectedTime)
                    scoreDef.frameAlreadySeeked = shouldSetFrameAlreadySeeked

                    // Just set to identify index changes
                    scoreDef.isChanged = true
                })
            } else {

                // Only update the score definition of the given index
                newScores[index].time = roundNumber(selectedTime)
                newScores[index].frameAlreadySeeked = shouldSetFrameAlreadySeeked

                // Just set to identify index changes
                newScores[index].isChanged = true
            }

            // Setting the time of the score definition might lead to an order change, which needs to be reflected in the tabs
            newScores.sort((s1, s2) => s1.time - s2.time)

            // Set the new scores in a sorted way, and reset the flag to false again
            setScoreDefinitions(newScores.map((sd) => {
                return {
                    ...sd,
                    isChanged: false
                }
            }))
            editedScoredScene.current.scoreDefinitions = newScores

            // Set the index, so that the tab becomes active (for point stop it does not makes sense as no scores with different times can exist)
            if (disciplineStructure.mode !== 'point_stop') {
                setActiveScoreDefinitionIndex(newScores.findIndex((sd) => sd.isChanged))
            }
        }
    }

    const handleChangeAthlete = (event, index) => {
        const newScores = [...scoreDefinitions]
        newScores[index].athlete = newScores[index].athlete === 'blue' ? 'red' : 'blue'
        setScoreDefinitions(newScores)

        editedScoredScene.current.scoreDefinitions = newScores
    }

    const handleChangeTechnique = (event, index) => {
        // Technique might be undefined in case the user clicks on the grouping menu header
        if (event.target.value !== undefined && event.target.value !== '') {
            const newScores = [...scoreDefinitions]
            newScores[index].technique = event.target.value
            newScores[index].score = disciplineStructure.scores.find((t) => t.technique === event.target.value).score // Change also the score based on the technique
            newScores[index].techniqueFreeText = null // Reset a possibly available free text as well (will be checked in BE as well)
            setScoreDefinitions(newScores)

            editedScoredScene.current.scoreDefinitions = newScores
        }
    }

    const handleChangeTechniqueFreeText = (value, index) => {
        const newScores = [...scoreDefinitions]
        newScores[index].techniqueFreeText = value
        setScoreDefinitions(newScores)

        editedScoredScene.current.scoreDefinitions = newScores
    }

    const handleTogglePlaybackRate = (event) => {
        setIsSlowPlaybackRate(event.target.checked)
        if (event.target.checked) {
            setDefaultPlaybackRate(0.3)
        } else {
            setDefaultPlaybackRate(1.0)
        }
    }

    const handleChangeScoreDefinitionTab = (event, value) => {
        setActiveScoreDefinitionIndex(value)
        jumpToScoreDefinition(value)
    }

    const showPreview = (scoreDefinition) => {
        setPreviewInProgress(true)
        videoPlayer.current.pause()
            .then(() => {
                return videoPlayer.current.setPlaybackRate(0.3)
            })
            .then((playbackRate) => {
                return videoPlayer.current.setCurrentTime(scoreDefinition.time - 0.5)
            })
            .then((seconds) => {

                // Reset the current highlighting directly drawn on the video (otherwise it will be shown permanently in the feedback preview)
                setActiveDrawingScoreDefinition(null)
                setVideoProgressInHighlightingRange(false)

                previewScoreDefinition.current = scoreDefinition


                return videoPlayer.current.play()
            })
    }

    /*
    Highlighting handlers...
     */
    const handleEnterDrawingMode = (scoreDefinition) => {
        setDrawingMode(true)
        setActiveDrawingScoreDefinition(scoreDefinition)

        videoPlayer.current.setCurrentTime(scoreDefinition.time)
    }

    const handleLeaveDrawingMode = () => {
        setDrawingMode(false)

        // Set the video progress to the actual score time (will trigger the rendering of the drawing on the normal video)
        videoPlayer.current.setCurrentTime(activeDrawingScoreDefinition.time)
    }

    const handleSaveDrawing = (visibilityStart, visibilityEnd, drawingDefinitions) => {
        const newScores = [...scoreDefinitions]
        activeDrawingScoreDefinition.highlighting = drawingDefinitions?.length > 0
            ? {
                visibilityStart: visibilityStart,
                visibilityEnd: visibilityEnd,
                drawing: drawingDefinitions
            }
            : null

        setScoreDefinitions(newScores)

        editedScoredScene.current.scoreDefinitions = newScores
    }

    const handleDeleteDrawing = () => {
        const newScores = [...scoreDefinitions]
        activeDrawingScoreDefinition.highlighting = null
        setScoreDefinitions(newScores)

        editedScoredScene.current.scoreDefinitions = newScores
    }


    /*
    Blurring handlers...
     */
    const handleEnterBlurringMode = () => {
        // Reset the current highlighting directly drawn on the video (otherwise it will be shown in the edit blurring mode)
        setActiveDrawingScoreDefinition(null)
        setVideoProgressInHighlightingRange(false)

        setBlurDrawingMode(true)

        if (blurringDefinition?.visibilityStart) {
            videoPlayer.current.setCurrentTime(blurringDefinition.visibilityStart)
        } else {
            videoPlayer.current.setCurrentTime(scoreDefinitions[0].time)
        }
    }

    const handleLeaveBlurringMode = () => {
        setBlurDrawingMode(false)
    }

    const handleSaveBlurring = (visibilityStart, visibilityEnd, blurringDefinitions) => {
        setBlurDrawingMode(false)

        const newBlurring = blurringDefinitions?.length > 0
            ? {
                visibilityStart: visibilityStart,
                visibilityEnd: visibilityEnd,
                drawing: blurringDefinitions
            }
            : null

        setBlurringDefinition(newBlurring)

        editedScoredScene.current = {
            ...editedScoredScene.current,
            blurring: newBlurring
        }
    }

    const handleDeleteBlurring = () => {
        setBlurringDefinition(null)

        editedScoredScene.current = {
            ...editedScoredScene.current,
            blurring: null
        }
    }

    return {
        // Form states
        difficulty,
        mainFocus,
        trailingPeriod,
        athleteLeftPosition,
        videoProgress,
        videoRange,
        scoreDefinitions,

        // Screen control
        screenReady,

        // Video utils
        originalVideoDuration,
        videoPlaying,

        // Playback rate
        handleTogglePlaybackRate,
        isSlowPlaybackRate,

        // Dimensions of the video iFrame (used for overlaying the drawing area)
        canvasWidth,
        canvasHeight,
        canvasLeft,
        canvasTop,

        // Helper functions for editing the score definitions
        addScoreDefinition,
        addConcurrentScoreDefinition,
        removeScoreDefinition,
        pictureForward,
        pictureBack,
        jumpToScoreDefinition,

        // Fast forward / fast rewind
        fastForward,
        fastRewind,

        // Handlers for updating the states and scored scene reference according to the user inputs
        handleChangeAthlete,
        handleChangeTechnique,
        handleChangeTechniqueFreeText,
        handleChangeDifficulty,
        handleChangeMainFocus,
        handleChangeTrailingPeriod,
        switchAthleteLeftPosition,

        // Video controls
        updateVideoRange,
        updateVideoProgress,
        setCurrentVideoTimeInScoreDefinition,
        playVideo,
        pauseVideo,
        fullScreenMode,
        setFullScreenMode,

        // Drawing
        activeDrawingScoreDefinition,
        drawingMode,
        previewInProgress,
        videoProgressInHighlightingRange,
        showPreview,
        handleEnterDrawingMode,
        handleLeaveDrawingMode,
        handleSaveDrawing,
        handleDeleteDrawing,

        // Blurring
        blurringDefinition,
        blurDrawingMode,
        showBlurPreview,
        handleEnterBlurringMode,
        handleLeaveBlurringMode,
        handleSaveBlurring,
        handleDeleteBlurring,

        // Video dimensions
        videoHeight,
        videoWidth,

        // Tabs
        activeScoreDefinitionIndex,
        handleChangeScoreDefinitionTab,

        // Video cut slider width for visualizing already created scored scene ranges
        videoCutSliderWidth
    }
}
export default useGeneralEditScoredSceneForm;